import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Undo2, TrendingDown, AlertTriangle, Download, 
  FileSpreadsheet, Users, Package, Store, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReturnIQDashboard = () => {
  const [searchParams] = useSearchParams();
  const runId = searchParams.get('run_id');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);
  const { languageConfig } = useLanguage();

  useEffect(() => {
    fetchDashboardData();
  }, [runId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/agent-dashboards/return-iq`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (dataType, label) => {
    try {
      setExporting(dataType);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/agent-dashboards/return-iq/export?data_type=${dataType}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dataType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${label} exported successfully`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    if (typeof value === 'string' && value.startsWith('₹')) return value;
    const num = parseFloat(value);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (error || !data?.hasData) {
    return (
      <Card className="m-6">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-lg text-slate-600">{error || 'No ReturnIQ analysis found. Please run the agent first.'}</p>
        </CardContent>
      </Card>
    );
  }

  const overview = data?.returnOverview || {};
  const highRiskSKUs = data?.highRiskSKUs || [];
  const highRiskCustomers = data?.highRiskCustomers || [];
  const returnsByReason = data?.returnsByReason || [];
  const returnsByCategory = data?.returnsByCategory || [];

  return (
    <div className="space-y-6 p-6" data-testid="return-iq-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Undo2 className="h-6 w-6 text-orange-600" />
            <TranslatedText text="ReturnIQ Dashboard" cacheKey="returniq:title" />
          </h1>
          <p className="text-slate-500 text-sm mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Returns Risk & Root-Cause Intelligence" cacheKey="returniq:subtitle" />
          </p>
        </div>
        <Button onClick={() => handleExport('all_returns', 'All Returns')} disabled={exporting === 'all_returns'}>
          <Download className="h-4 w-4 mr-2" />
          Export Full Analysis
        </Button>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Undo2 className="h-4 w-4" />
              Total Returns
            </div>
            <p className="text-2xl font-bold text-slate-900">{overview.totalReturns || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <TrendingDown className="h-4 w-4" />
              Return Value
            </div>
            <p className="text-2xl font-bold text-red-600">{overview.totalReturnValueFormatted || formatCurrency(overview.totalReturnValue)}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Package className="h-4 w-4 text-red-500" />
              High Risk SKUs
            </div>
            <p className="text-2xl font-bold text-red-600">{overview.highRiskSKUsCount || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Users className="h-4 w-4 text-orange-500" />
              High Risk Customers
            </div>
            <p className="text-2xl font-bold text-orange-600">{overview.highRiskCustomersCount || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* High Risk SKUs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              High Risk SKUs
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('high_risk_skus', 'High Risk SKUs')}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {highRiskSKUs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">SKU ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-right font-semibold">Return Rate</th>
                    <th className="px-3 py-2 text-right font-semibold">Return Count</th>
                    <th className="px-3 py-2 text-right font-semibold">Return Value</th>
                    <th className="px-3 py-2 text-left font-semibold">Top Reason</th>
                    <th className="px-3 py-2 text-left font-semibold">Risk Level</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {highRiskSKUs.slice(0, 10).map((sku, idx) => (
                    <tr key={idx} className="hover:bg-red-50">
                      <td className="px-3 py-2 font-mono">{sku.skuId || '-'}</td>
                      <td className="px-3 py-2">{sku.skuName || '-'}</td>
                      <td className="px-3 py-2 text-right font-bold text-red-600">{sku.returnRatePct?.toFixed(1) || 0}%</td>
                      <td className="px-3 py-2 text-right">{sku.returnCount || 0}</td>
                      <td className="px-3 py-2 text-right">{sku.returnValueFormatted || formatCurrency(sku.returnValue)}</td>
                      <td className="px-3 py-2 text-sm">{sku.topReason || '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          sku.riskLevel === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {sku.riskLevel || 'HIGH'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">No high risk SKUs identified</p>
          )}
        </CardContent>
      </Card>

      {/* Returns by Reason */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Returns by Reason
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('returns_by_reason', 'Returns by Reason')}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {returnsByReason.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Reason</th>
                    <th className="px-3 py-2 text-left font-semibold">Group</th>
                    <th className="px-3 py-2 text-right font-semibold">Count</th>
                    <th className="px-3 py-2 text-right font-semibold">Value</th>
                    <th className="px-3 py-2 text-right font-semibold">% of Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {returnsByReason.slice(0, 10).map((reason, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{reason.reason || '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          reason.reasonGroup === 'QUALITY' ? 'bg-red-100 text-red-700' :
                          reason.reasonGroup === 'PROCESS' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {reason.reasonGroup || 'OTHER'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{reason.count || 0}</td>
                      <td className="px-3 py-2 text-right">{reason.valueFormatted || formatCurrency(reason.value)}</td>
                      <td className="px-3 py-2 text-right">{reason.pctOfTotal?.toFixed(1) || 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">No return reason data available</p>
          )}
        </CardContent>
      </Card>

      {/* High Risk Customers */}
      {highRiskCustomers.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-600" />
                High Risk Customers
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('high_risk_customers', 'High Risk Customers')}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-orange-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Customer ID</th>
                    <th className="px-3 py-2 text-right font-semibold">Return Count</th>
                    <th className="px-3 py-2 text-right font-semibold">Return Value</th>
                    <th className="px-3 py-2 text-right font-semibold">Return Rate</th>
                    <th className="px-3 py-2 text-left font-semibold">Risk Type</th>
                    <th className="px-3 py-2 text-left font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {highRiskCustomers.slice(0, 10).map((customer, idx) => (
                    <tr key={idx} className="hover:bg-orange-50">
                      <td className="px-3 py-2 font-mono">{customer.customerId || '-'}</td>
                      <td className="px-3 py-2 text-right">{customer.returnCount || 0}</td>
                      <td className="px-3 py-2 text-right">{customer.returnValueFormatted || formatCurrency(customer.returnValue)}</td>
                      <td className="px-3 py-2 text-right font-bold text-orange-600">{customer.returnRatePct?.toFixed(1) || 0}%</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700">
                          {customer.riskType || 'HIGH_RETURNER'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm">{customer.flag || 'Review'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Plan */}
      {data?.actionPlan && Object.keys(data.actionPlan).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Recommended Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.actionPlan.P0_Immediate?.length > 0 && (
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-red-600 mb-2">P0 - Immediate</h4>
                  {data.actionPlan.P0_Immediate.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-red-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
              {data.actionPlan.P1_ThisWeek?.length > 0 && (
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-orange-600 mb-2">P1 - This Week</h4>
                  {data.actionPlan.P1_ThisWeek.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-orange-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
              {data.actionPlan.P2_NextCycle?.length > 0 && (
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-blue-600 mb-2">P2 - Next Cycle</h4>
                  {data.actionPlan.P2_NextCycle.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-blue-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReturnIQDashboard;
